/**
 * Result display configuration & honest-status derivation.
 *
 * Maps the REAL backend statuses (`@/types/execution-results`) to a clear,
 * plain-language vocabulary and derives the "Stalled"/"Blocked" honest states
 * that the raw backend status doesn't express on its own. Tone strings map to
 * the platform's slate-dominant Tailwind palette (color = meaning only).
 */

import {
  Mail, Phone, Database, Linkedin, Send, Bot, UserSearch, Timer, Repeat,
  Search, FileSearch, Bell, Slack, Video, Sheet, FolderSearch, Calendar,
  Webhook, MousePointerClick, Mic, Mailbox, GitBranch, Globe, Play,
} from 'lucide-react';
import type { FC } from 'react';
import type {
  Run, RunExecution, RunStatus, ExecutionStatus, StepProgress, DailyCapHold,
} from '@/types/execution-results';
import { getPlayDisplayName } from '@/utils/playDisplayNames';
import { isNativeLinkedInSequence, nativeSequenceLabel } from '@/utils/nativeLinkedInSequence';
import { HEYREACH_STEP_CATALOG } from '../../LeftPanelNodeOverview/HeyReachNodeOverview';
import {
  displayTimezone,
  formatAbsolute,
  formatEstimatedDate,
  getStepScheduleTimezone,
  isCurrentlyOutsideBusinessHours,
  stepUsesBusinessHours,
  tzAbbrev,
} from './scheduleProjection';

/** Vendor-neutral step name for messages/meta that quote a step by name. */
const stepName = (e: RunExecution): string => getPlayDisplayName(e.play_type, e.play_name);

export type Tone =
  | 'emerald' | 'amber' | 'red' | 'blue' | 'indigo'
  | 'violet' | 'purple' | 'slate' | 'rose' | 'teal' | 'sky';

/** Display status vocabularies (honest, plain-language). */
export type RunHealth =
  | 'delivered' | 'partial' | 'stalled' | 'scheduled' | 'running' | 'failed' | 'pending';
export type StepHealth =
  | 'delivered' | 'partial' | 'failed' | 'running' | 'sending' | 'waiting' | 'queued' | 'blocked' | 'skipped';

interface StatusMeta {
  label: string;
  tone: Tone;
  desc: string;
}

/**
 * Tone → Tailwind class fragments. All literal strings so the JIT compiler
 * keeps them. Soft tints for pills/tiles; solid for dots/accents.
 */
export const TONE: Record<Tone, {
  pill: string; dot: string; tile: string; solid: string; bar: string; text: string;
}> = {
  emerald: { pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', tile: 'bg-emerald-50 text-emerald-600', solid: 'text-emerald-500', bar: 'bg-emerald-500', text: 'text-emerald-700' },
  amber:   { pill: 'bg-amber-50 text-amber-700 border-amber-200',       dot: 'bg-amber-500',   tile: 'bg-amber-50 text-amber-600',     solid: 'text-amber-500',   bar: 'bg-amber-500',   text: 'text-amber-700' },
  red:     { pill: 'bg-red-50 text-red-700 border-red-200',             dot: 'bg-red-500',     tile: 'bg-red-50 text-red-600',         solid: 'text-red-500',     bar: 'bg-red-500',     text: 'text-red-700' },
  blue:    { pill: 'bg-blue-50 text-blue-700 border-blue-200',          dot: 'bg-blue-500',    tile: 'bg-blue-50 text-blue-600',       solid: 'text-blue-500',    bar: 'bg-blue-500',    text: 'text-blue-700' },
  indigo:  { pill: 'bg-indigo-50 text-indigo-700 border-indigo-200',    dot: 'bg-indigo-500',  tile: 'bg-indigo-50 text-indigo-600',   solid: 'text-indigo-500',  bar: 'bg-indigo-500',  text: 'text-indigo-700' },
  violet:  { pill: 'bg-violet-50 text-violet-700 border-violet-200',    dot: 'bg-violet-500',  tile: 'bg-violet-50 text-violet-600',   solid: 'text-violet-500',  bar: 'bg-violet-500',  text: 'text-violet-700' },
  purple:  { pill: 'bg-purple-50 text-purple-700 border-purple-200',    dot: 'bg-purple-500',  tile: 'bg-purple-50 text-purple-600',   solid: 'text-purple-500',  bar: 'bg-purple-500',  text: 'text-purple-700' },
  slate:   { pill: 'bg-slate-100 text-slate-600 border-slate-200',      dot: 'bg-slate-400',   tile: 'bg-slate-100 text-slate-500',    solid: 'text-slate-400',   bar: 'bg-slate-400',   text: 'text-slate-600' },
  rose:    { pill: 'bg-rose-50 text-rose-700 border-rose-200',          dot: 'bg-rose-500',    tile: 'bg-rose-50 text-rose-600',       solid: 'text-rose-500',    bar: 'bg-rose-500',    text: 'text-rose-700' },
  teal:    { pill: 'bg-teal-50 text-teal-700 border-teal-200',          dot: 'bg-teal-500',    tile: 'bg-teal-50 text-teal-600',       solid: 'text-teal-500',    bar: 'bg-teal-500',    text: 'text-teal-700' },
  sky:     { pill: 'bg-cyan-50 text-cyan-700 border-cyan-200',             dot: 'bg-cyan-500',     tile: 'bg-cyan-50 text-cyan-600',         solid: 'text-cyan-500',     bar: 'bg-cyan-500',     text: 'text-cyan-700' },
};

/** Run-level status meanings (display order matters for the legend). */
export const RUN_STATUS_META: Record<RunHealth, StatusMeta> = {
  delivered: { label: 'Delivered', tone: 'emerald', desc: 'The campaign ran end-to-end. Every step completed for every eligible contact.' },
  partial:   { label: 'Partial',   tone: 'amber',   desc: 'The run finished, but some contacts didn’t receive every step (a bounce, skip, or failure along the way).' },
  stalled:   { label: 'Stalled',   tone: 'red',     desc: 'The run stopped mid-sequence — a step failed and the steps after it never ran. Needs your attention.' },
  scheduled: { label: 'Scheduled', tone: 'indigo',  desc: 'Paused on a delay step. It will continue automatically when the timer completes. On track.' },
  running:   { label: 'Running',   tone: 'blue',    desc: 'The run is in progress right now.' },
  failed:    { label: 'Failed',    tone: 'red',     desc: 'The run failed at the start and produced no results.' },
  pending:   { label: 'Pending',   tone: 'slate',   desc: 'Queued — hasn’t started executing yet.' },
};

/** Step-level status meanings. */
export const STEP_STATUS_META: Record<StepHealth, StatusMeta> = {
  delivered: { label: 'Delivered', tone: 'emerald', desc: 'Every contact was processed successfully at this step.' },
  partial:   { label: 'Partial',   tone: 'amber',   desc: 'Some contacts succeeded; others failed, bounced, or were skipped. See the counts.' },
  failed:    { label: 'Failed',    tone: 'red',     desc: 'This step errored and produced no successful output. A reason is shown.' },
  running:   { label: 'Running',   tone: 'blue',    desc: 'This step is executing right now.' },
  sending:   { label: 'Sending',   tone: 'blue',    desc: 'Emails are still going out. Sends are paced across your mailboxes, so a large step finishes over minutes or hours — the counts update as they land.' },
  waiting:   { label: 'Waiting',   tone: 'indigo',  desc: 'A timed delay is in progress. Contacts are held here until the timer completes — this is normal.' },
  queued:    { label: 'Queued',    tone: 'slate',   desc: 'Hasn’t started yet. Waiting for the previous step to finish.' },
  blocked:   { label: 'Blocked',   tone: 'rose',    desc: 'Could not start because an earlier step failed. These contacts are stuck until you re-run.' },
  skipped:   { label: 'Skipped',   tone: 'slate',   desc: 'Intentionally not run for these contacts (e.g. they already replied or were filtered out).' },
};

/** Play type → tile icon + tone + short label, covering all known play types. */
export const STEP_TYPE_META: Record<string, { label: string; icon: FC<{ size?: number; className?: string }>; tone: Tone }> = {
  email_campaign:      { label: 'Email',        icon: Mail,               tone: 'blue' },
  outreach_campaign:   { label: 'Outreach',     icon: Phone,              tone: 'purple' },
  crm_add:             { label: 'CRM Sync',     icon: Database,           tone: 'emerald' },
  linkedin_enrichment: { label: 'LinkedIn',     icon: Linkedin,           tone: 'sky' },
  heyreach_campaign:   { label: 'LinkedIn Outreach', icon: Send,          tone: 'indigo' },
  ai_agent:            { label: 'AI Agent',     icon: Bot,                tone: 'amber' },
  prospect:            { label: 'Prospecting',  icon: UserSearch,         tone: 'teal' },
  apollo_prospect:     { label: 'Prospecting',  icon: UserSearch,         tone: 'teal' },
  delay:               { label: 'Delay',        icon: Timer,              tone: 'slate' },
  loop:                { label: 'Loop',         icon: Repeat,             tone: 'slate' },
  serp_search:         { label: 'Search',       icon: Search,            tone: 'blue' },
  find_email:          { label: 'Find Email',   icon: Mail,               tone: 'blue' },
  find_materials:      { label: 'Materials',    icon: FileSearch,         tone: 'violet' },
  notification_email:  { label: 'Notify',       icon: Bell,               tone: 'amber' },
  slack_alert:         { label: 'Slack',        icon: Slack,              tone: 'violet' },
  teams_alert:         { label: 'Teams',        icon: Bell,               tone: 'violet' },
  heygen_video:        { label: 'Video',        icon: Video,              tone: 'purple' },
  video_editor:        { label: 'Video Edit',   icon: Video,              tone: 'purple' },
  google_sheets:       { label: 'Sheets',       icon: Sheet,              tone: 'emerald' },
  google_drive_scan:   { label: 'Drive Scan',   icon: FolderSearch,       tone: 'emerald' },
  calendar_scan:       { label: 'Calendar',     icon: Calendar,           tone: 'blue' },
  calendar_worker:     { label: 'Calendar',     icon: Calendar,           tone: 'blue' },
  webhook:             { label: 'Webhook',      icon: Webhook,            tone: 'slate' },
  browser_automation:  { label: 'Browser',      icon: Globe,              tone: 'slate' },
  gong_transcript:     { label: 'Gong',         icon: Mic,                tone: 'violet' },
  voicemail_drop:      { label: 'Voicemail',    icon: Mic,                tone: 'purple' },
  email_scan:          { label: 'Email Scan',   icon: Mailbox,            tone: 'blue' },
  mailbox_warmup:      { label: 'Warmup',       icon: Mailbox,            tone: 'amber' },
  referral_source:     { label: 'Referral',     icon: GitBranch,          tone: 'teal' },
  episode_discovery:   { label: 'Podcasts',     icon: Search,            tone: 'violet' },
  call_website_visitors: { label: 'Web Visitors', icon: MousePointerClick, tone: 'blue' },
};

export const DEFAULT_STEP_TYPE = { label: 'Step', icon: Play, tone: 'slate' as Tone };

export function getStepType(playType: string) {
  return STEP_TYPE_META[playType] || DEFAULT_STEP_TYPE;
}

const HEYREACH_ACTION_LABEL_BY_TYPE: Record<string, string> = Object.fromEntries(
  HEYREACH_STEP_CATALOG.map((entry) => [entry.type, entry.label]),
);

/**
 * The specific HeyReach action a heyreach_campaign step performs — "View profile",
 * "Send connection request", "Send message", etc. Every heyreach_campaign row shares
 * the same generic step-type label ("LinkedIn Outreach"), which doesn't distinguish
 * a profile view from a connection request from a follow-up message. Reuses the same
 * catalog the node editor uses, so the wording matches what the operator configured.
 */
export function getHeyReachActionLabel(customActionDetails: Record<string, unknown> | null | undefined): string | null {
  // A native LinkedIn sequence is several touches; its first step alone would read as a lone invite.
  if (isNativeLinkedInSequence(customActionDetails)) return nativeSequenceLabel(customActionDetails);
  const steps = customActionDetails?.steps;
  const actionType = Array.isArray(steps) && steps[0] && typeof steps[0] === 'object'
    ? (steps[0] as { action_type?: string }).action_type
    : undefined;
  if (!actionType) return null;
  return HEYREACH_ACTION_LABEL_BY_TYPE[actionType] || null;
}

/* ------------------------------------------------------------------ */
/* Metric helpers — execution.metrics is an untyped Record            */
/* ------------------------------------------------------------------ */

export function num(rec: Record<string, unknown> | null | undefined, ...keys: string[]): number {
  if (!rec) return 0;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return 0;
}

/**
 * Workflow-control play types that don't operate on contacts. The backend
 * (`runHistory.service.extractMetrics`) returns total_contacts=0 for these, so
 * the UI must show "—", not "0".
 */
export const CONTROL_PLAY_TYPES = new Set(['delay', 'loop', 'slack_alert', 'teams_alert', 'email_scan']);

export interface StepMetrics {
  isControl: boolean;
  /** Robust contact count: total_contacts is often NULL — fall back to people_count/results/successful. */
  contacts: number;
  /** Numeric "done" for KPI roll-ups (emails sent / processed / synced). Always 0 for a test run's email step. */
  done: number;
  /** Emails a test run generated for preview and did not send. 0 outside test runs. */
  previewed: number;
  /**
   * Emails handed to the send queue over the whole step, sent or not. A queued total, not a
   * count of emails still waiting: a finished run keeps its number.
   */
  scheduled: number;
  /** Emails saved as drafts (save_in_draft campaigns) rather than sent. */
  drafted: number;
  /**
   * Emails queued at this step that have reached no recipient outcome yet — the "N pending"
   * in the summary. 0 for every non-email step and for a step that has finished sending.
   */
  outstanding: number;
  failed: number;
  skipped: number;
  /** Per-contact engagement, summed from the email step's results[]. */
  opens: number;
  clicks: number;
  replies: number;
  hasEngagement: boolean;
  /**
   * Plain-language result summary, ported 1:1 from WorkflowExecutionSheet's
   * getMetricsSummary — the verified source of truth. e.g. "78 scheduled",
   * "44 sent · 6 pending · 1 bounced", "78 contacts · 78 processed".
   */
  summary: string;
}

/**
 * Skip-reason labels — the single source of truth for both the Result tab and
 * WorkflowExecutionSheet. Keys mirror `runHistory.service.extractMetrics`
 * (per-recipient skips + email_campaign agent_metrics fallback).
 */
const SKIP_REASON_LABELS: Record<string, string> = {
  skipped_invalid: 'invalid email', skipped_recent: 'recently emailed',
  skipped_duplicate: 'duplicate', skipped_replied: 'already replied',
  // Per-recipient statuses the email send worker records (email_send_history). ContactCard spells
  // each one out on the row; the step summary only needs the short form.
  skipped_customer_excluded: 'existing customer', skipped_unsubscribed: 'unsubscribed',
  skipped_audience_duplicate: 'duplicate in campaign', skipped_suppressed: 'suppressed',
  skipped_audience_not_published: 'campaign paused',
  skipped_suppression_unavailable: 'compliance check unavailable',
  no_recipients: 'no recipients', no_eligible_recipients: 'no eligible recipients',
  no_valid_emails: 'no valid emails', user_skipped: 'skipped by user',
  user_cancelled: 'cancelled by user', user_paused: 'paused by user',
  parent_skipped: 'parent step skipped', skipped: 'skipped',
  // Suppression sources, written by the agent worker's pre-filter (audience-processor). The
  // raw keys are database values — "manual delete" told a rep nothing about what happened to
  // their contact.
  manual_delete: 'removed from the list', do_not_contact: 'do not contact',
  company_mismatch_repair: 'wrong company', suppressed: 'suppressed',
};
/** Humanize a skip-reason key, falling back to a readable form for unknown keys. */
function formatSkipReason(key: string): string {
  return SKIP_REASON_LABELS[key] || key.replace(/^skipped_/, '').replace(/_/g, ' ');
}
function renderSkipLabel(count: number, reasons: Record<string, number> | undefined, fallback = 'skipped'): string {
  if (count <= 0) return '';
  const entries = Object.entries(reasons || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return `${count} ${fallback}`;
  if (entries.length === 1) return `${count} ${fallback} (${formatSkipReason(entries[0][0])})`;
  const top = entries.slice(0, 2).map(([k, n]) => `${n} ${formatSkipReason(k)}`).join(' · ');
  const rest = entries.slice(2).reduce((a, [, n]) => a + n, 0);
  return `${count} ${fallback} (${top}${rest > 0 ? ` · ${rest} other` : ''})`;
}

interface SkipTally {
  total: number;
  reasons: Record<string, number>;
}

const EMAIL_AGENT_SKIP_FIELDS: ReadonlyArray<[field: string, reason: string]> = [
  ['emails_skipped_invalid', 'skipped_invalid'],
  ['emails_skipped_recent', 'skipped_recent'],
  ['emails_skipped_duplicate', 'skipped_duplicate'],
  ['emails_skipped_customer_excluded', 'skipped_customer_excluded'],
];

function tallyFromReasons(reasons: Record<string, number>): SkipTally {
  const kept: Record<string, number> = {};
  let total = 0;
  for (const [reason, n] of Object.entries(reasons)) {
    if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) continue;
    kept[reason] = n;
    total += n;
  }
  return { total, reasons: kept };
}

/**
 * Every recipient an email step skipped, whichever field reports them.
 *
 * The email dispatcher only ever writes `emails_skipped_recent` / `emails_skipped_invalid` into
 * agent_metrics, and the per-recipient send worker never rolls its own skips (duplicate,
 * existing customer, unsubscribed…) back into those counters. Those skips reach the screen as
 * the execution's top-level `skipped` + `skip_reasons` (the server counts them over every
 * recipient result) and as `skipped_*` statuses on the per-recipient results list. Reading the
 * agent counters alone left 34 duplicates out of the count, so the step summed them up as
 * "36 pending" days after the run finished (Jumio, 2026-10-19).
 *
 * The three sources describe the same recipients, so they are never added together: the
 * fullest one wins. The results list can be capped, which is why the server's total is
 * preferred over recounting it.
 *
 * The results list is counted by recipient, not by row. When the send worker redelivers a
 * recipient it already sent to, it leaves a `skipped_duplicate` audit row next to the sent row,
 * and the server's row count then reports that recipient as skipped although the email went
 * out. A recipient with a sent, replied, bounced, drafted or failed row is never a skip, and a
 * recipient with two skipped rows is one skip. Those audit rows are also taken off the server's
 * total, which counts the same rows (99 steps, 172 rows over-counted in the prod replay of
 * 2026-10-19).
 */
/** Result-row statuses that settle a recipient: an email that reached them is not a skip. */
const SETTLED_RECIPIENT_STATUSES: ReadonlySet<string> = new Set([
  'sent', 'replied', 'bounced', 'draft', 'drafted', 'failed',
]);

function recipientKey(r: Record<string, unknown>): string | null {
  const person = (r.person && typeof r.person === 'object' ? r.person : {}) as Record<string, unknown>;
  const contact = (r.contact && typeof r.contact === 'object' ? r.contact : {}) as Record<string, unknown>;
  for (const v of [r.email, r.recipient_email, person.email, contact.email]) {
    if (typeof v === 'string' && v.trim()) return v.trim().toLowerCase();
  }
  return null;
}

export function countEmailSkips(exec: Pick<RunExecution, 'metrics' | 'results'>): SkipTally {
  const m = (exec.metrics || {}) as Record<string, unknown>;
  const agent = (m.agent_metrics || {}) as Record<string, unknown>;

  const fromAgent = tallyFromReasons(Object.fromEntries(
    EMAIL_AGENT_SKIP_FIELDS.map(([field, reason]) => [reason, num(agent, field)]),
  ));

  // Per-recipient results, one count per recipient. Rows without an address can't be matched
  // up, so each of those still counts on its own.
  const perRecipient: Record<string, number> = {};
  // Skipped rows the list itself shows are not skips: redelivery audit rows beside a settled
  // row, or a second skipped row for the same recipient. The server counted these rows too.
  const auditRows: Record<string, number> = {};
  if (Array.isArray(exec.results)) {
    const settled = new Set<string>();
    const skippedByRecipient = new Map<string, string[]>();
    for (const raw of exec.results) {
      const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      const status = String(r.status ?? r.state ?? '').toLowerCase();
      const isSkip = status === 'skipped' || status.startsWith('skipped_');
      const key = recipientKey(r);
      if (isSkip) {
        if (key == null) perRecipient[status] = (perRecipient[status] || 0) + 1;
        else skippedByRecipient.set(key, [...(skippedByRecipient.get(key) || []), status]);
      } else if (key != null && SETTLED_RECIPIENT_STATUSES.has(status)) {
        settled.add(key);
      }
    }
    for (const [key, statuses] of skippedByRecipient) {
      const [first, ...rest] = statuses;
      if (settled.has(key)) {
        for (const status of statuses) auditRows[status] = (auditRows[status] || 0) + 1;
        continue;
      }
      perRecipient[first] = (perRecipient[first] || 0) + 1;
      for (const status of rest) auditRows[status] = (auditRows[status] || 0) + 1;
    }
  }
  const fromResults = tallyFromReasons(perRecipient);
  const auditTotal = Object.values(auditRows).reduce((a, n) => a + n, 0);

  const topLevel = Math.max(0, num(m, 'skipped') - auditTotal);
  const rawTopReasons = (m.skip_reasons && typeof m.skip_reasons === 'object'
    ? m.skip_reasons
    : {}) as Record<string, number>;
  const topReasons = tallyFromReasons(Object.fromEntries(
    Object.entries(rawTopReasons).map(([reason, n]) => [reason, typeof n === 'number' ? n - (auditRows[reason] || 0) : n]),
  ));
  const fromServer: SkipTally = {
    total: Math.max(topLevel, topReasons.total),
    reasons: topReasons.reasons,
  };

  // Fullest count wins; on a tie, the source with a reason breakdown.
  return [fromServer, fromResults, fromAgent].reduce((best, next) => {
    if (next.total > best.total) return next;
    if (next.total === best.total && Object.keys(best.reasons).length === 0) return next;
    return best;
  });
}

/**
 * Derive accurate, display-ready metrics for a single execution.
 *
 * Mirrors the production-proven `WorkflowExecutionSheet.getMetricsSummary`
 * (verified against the prod DB): contact count falls back to people_count when
 * total_contacts is NULL; email steps read `agent_metrics` (emails_scheduled /
 * emails_sent) so in-flight runs show "78 scheduled" instead of "0"; ai_agent
 * batch sequencers show "N contacts · M processed". Engagement (opens/clicks/
 * replies) is summed from `results[]`.
 */
/**
 * Emails queued at this step that have not reached any recipient outcome yet.
 *
 * Shared by the step's summary line and its status so the two cannot disagree — a step
 * reading "Delivered" beside its own "20 pending" is exactly what a customer reported on
 * 2026-10-29. A bounce and a skip are both settled: the first went out, the second never
 * entered the queue.
 */
function outstandingFromCounts(counts: {
  scheduled: number; drafted: number; sent: number; failed: number; bounced: number; skipped: number;
}): number {
  const { scheduled, drafted, sent, failed, bounced, skipped } = counts;
  return Math.max(0, scheduled - drafted - sent - failed - bounced - skipped);
}

/**
 * How long after dispatch a step's unsent emails are still believed to be in the queue.
 * Matches the server's own window (runHistory.service STILL_SENDING_MAX_AGE_MS) so the run
 * badge and the step badge flip together. Past it, a step reads exactly as it always did.
 */
const STILL_SENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Is this email step still working through its send queue?
 *
 * An email step is recorded 'completed' when its FIRST recipient settles, not its last, and
 * sends are then drip-fed under the per-mailbox throttle. Prod run #3 of audience 10999 was
 * dispatched at 06:00Z and sent its last email at 07:46Z; for that whole window the step
 * showed a green "Delivered" tick beside a summary that said "10 sent · 20 pending".
 */
export function isStillSendingEmails(exec: Pick<RunExecution, 'play_type' | 'status' | 'metrics' | 'executed_at' | 'results' | 'test_mode'>): boolean {
  if (exec.play_type !== 'email_campaign' || exec.test_mode) return false;
  const m = (exec.metrics || {}) as Record<string, unknown>;
  const agent = (m.agent_metrics || {}) as Record<string, unknown>;
  const scheduled = num(agent, 'emails_scheduled');
  if (scheduled <= 0) return false;
  const outstanding = outstandingFromCounts({
    scheduled,
    drafted: num(agent, 'emails_drafted'),
    sent: num(agent, 'emails_sent'),
    failed: num(agent, 'emails_failed'),
    bounced: num(m, 'bounced'),
    skipped: countEmailSkips(exec as RunExecution).total,
  });
  if (outstanding <= 0) return false;
  // Without a dispatch time we cannot tell a draining queue from an abandoned one, and
  // guessing "still sending" would strand the step on a non-terminal badge forever.
  const dispatchedAt = exec.executed_at ? new Date(exec.executed_at).getTime() : NaN;
  if (!Number.isFinite(dispatchedAt)) return false;
  return (Date.now() - dispatchedAt) < STILL_SENDING_MAX_AGE_MS;
}

export function deriveStepMetrics(exec: RunExecution): StepMetrics {
  const m = (exec.metrics || {}) as Record<string, unknown>;
  const agent = (m.agent_metrics || {}) as Record<string, unknown>;
  const isControl = CONTROL_PLAY_TYPES.has(exec.play_type);

  const total = num(m, 'total_contacts');
  const people = exec.people_count ?? num(m, 'people_count');
  const resultsCount = Array.isArray(exec.results) ? exec.results.length : 0;
  const successful = num(m, 'successful');
  const failed = num(m, 'failed');
  const skipped = num(m, 'skipped');
  const replied = num(m, 'replied');
  const bounced = num(m, 'bounced');
  const skipReasons = m.skip_reasons as Record<string, number> | undefined;
  const contacts = Math.max(total, people, resultsCount, successful);

  const scheduled = num(agent, 'emails_scheduled');
  const emailsSent = num(agent, 'emails_sent');
  const emailsDrafted = num(agent, 'emails_drafted');
  const emailsFailed = num(agent, 'emails_failed');
  const emailSkips = exec.play_type === 'email_campaign' ? countEmailSkips(exec) : { total: 0, reasons: {} };
  const emailSkip = emailSkips.total;
  // Only the dispatcher's own counters say whether this step went through the email queue at
  // all; a step the pre-filter dropped entirely (no agent_metrics) keeps the generic wording.
  const agentSkip = EMAIL_AGENT_SKIP_FIELDS.reduce((acc, [field]) => acc + num(agent, field), 0);
  const capHold = exec.play_type === 'email_campaign' ? heldForDailyCap(exec) : null;
  const terminal = ['completed', 'failed', 'partial'].includes(exec.status);

  // Engagement from per-contact results, counted as EMAILS OPENED — one per contact who opened at
  // least once, never the number of times they opened. The Campaigns list counts the same thing
  // (`COUNT(DISTINCT esh.id)` over sends with an open), so a campaign's runs now add up to the
  // figure that list shows: FarEye's four runs read 12 + 21 + 23 + 23 = 79 of 139, which is exactly
  // the 79 (57%) on their list row. Summing `opens_count` instead counted open *events* against a
  // sends denominator, so a run could report more opens than it sent and no two screens agreed.
  // "How many times" is still answered per person — ContactCard renders "Opened 3x" from the raw
  // count — because that is a question about a contact, not about a run.
  //
  // The server's `metrics.engagement` covers every batch of the step in the run and isn't
  // capped like the per-contact list, so it wins when present. Older responses fall back to
  // counting the listed contacts.
  let opens = 0, clicks = 0, replies = 0, hasEngagement = false;
  const stepEngagement = exec.play_type === 'email_campaign'
    ? (m.engagement as Record<string, unknown> | undefined)
    : undefined;
  if (stepEngagement && typeof stepEngagement === 'object') {
    hasEngagement = true;
    opens = num(stepEngagement, 'opened');
    clicks = num(stepEngagement, 'clicked');
    replies = num(stepEngagement, 'replied');
  } else if (exec.play_type === 'email_campaign' && resultsCount > 0) {
    hasEngagement = true;
    for (const raw of exec.results) {
      const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      if (num(r, 'opens_count') > 0) opens += 1;
      if (num(r, 'clicks_count') > 0) clicks += 1;
      if ((r.reply as { has_reply?: boolean } | null)?.has_reply) replies += 1;
    }
  }

  let done = successful;
  let failedForDisplay = failed;
  let summary = '';

  if (exec.play_type === 'linkedin_enrichment') {
    const hasExplicitOutcomeMetrics = [
      'profile_api_enriched',
      'person_lookup_found',
      'original_csv_people',
      'not_found',
    ].some((key) => Object.prototype.hasOwnProperty.call(agent, key));
    const profileApiEnriched = num(agent, 'profile_api_enriched');
    const personLookupFound = num(agent, 'person_lookup_found');
    const explicitEnriched = profileApiEnriched + personLookupFound;
    const enriched = hasExplicitOutcomeMetrics ? explicitEnriched : successful;
    const notFound = Math.max(
      num(agent, 'not_found'),
      num(agent, 'original_csv_people'),
      contacts - enriched,
      0,
    );
    const otherFailures = Math.max(0, failed - notFound);
    const parts: string[] = [];
    // person_lookup "found" records are a distinct, less-complete outcome (LinkedIn
    // URL located but full profile unavailable) — ContactCard renders them with
    // their own "Profile found" pill, not "Enriched". Surface that same split here
    // so the step summary's "N enriched" always matches how many contact cards
    // actually say "Enriched".
    if (hasExplicitOutcomeMetrics && personLookupFound > 0) {
      if (profileApiEnriched > 0) parts.push(`${profileApiEnriched} enriched`);
      parts.push(`${personLookupFound} profile found`);
    } else if (enriched > 0) {
      parts.push(`${enriched} enriched`);
    }
    if (notFound > 0) parts.push(`${notFound} not found`);
    if (otherFailures > 0) parts.push(`${otherFailures} failed`);
    if (parts.length === 0 && contacts > 0) parts.push(`${contacts} processed`);
    summary = parts.join(' · ');
    done = enriched;
    failedForDisplay = Math.max(failed, notFound + otherFailures);
  }

  let previewed = 0;
  let outstanding = 0;
  if (exec.play_type === 'email_campaign' && exec.test_mode) {
    // A test run writes every email as a preview and sends none. Its rows count as `successful`
    // and its emails as `emails_scheduled`, so the live-run wording below read "3 scheduled" per
    // step and the run tile added them up as "Emails sent 12" (EcoOnline, 2026-10-23; ms#6616).
    // A preview is neither sent nor queued, so it gets its own count and never feeds `done`.
    // Only a written preview counts, so read the `test_run` rows themselves. Both `emails_scheduled`
    // and `successful` can include emails still being generated: with no rows yet, run details
    // fill `successful` from the scheduled counter. Rows are listed up to 2,000 per step, far above
    // any test run.
    // Still generating = scheduled emails with no settled row yet. Only rows count against
    // `emails_scheduled`: the suppression pre-filter's drops (in `emailSkip` via metrics.skipped)
    // never entered it, so subtracting them under-counted what was still being written.
    const rowStatuses = (Array.isArray(exec.results) ? exec.results : [])
      .map((row) => String((row as { status?: unknown } | null)?.status ?? '').toLowerCase());
    previewed = rowStatuses.filter((status) => status === 'test_run').length;
    const settledRows = rowStatuses.filter((status) => status === 'test_run' || status === 'failed'
      || status === 'error' || status === 'skipped' || status.startsWith('skipped_')).length;
    const generating = terminal ? 0 : Math.max(0, scheduled - settledRows);
    const parts: string[] = [];
    if (previewed > 0) parts.push(`${previewed} previewed`);
    if (generating > 0) parts.push(`${generating} generating`);
    if (emailsFailed > 0) parts.push(`${emailsFailed} failed`);
    if (emailSkip > 0) parts.push(renderSkipLabel(emailSkip, emailSkips.reasons, 'skipped'));
    done = 0;
    summary = parts.join(' · ');
  } else if (exec.play_type === 'email_campaign') {
    const hasAgent = scheduled + emailsSent + emailsDrafted + emailsFailed + agentSkip + replied + bounced
      + (capHold?.held ?? 0) > 0;
    if (hasAgent) {
      const parts: string[] = [];
      // Drafts (save_in_draft campaigns) are an outcome, not pending sends — surface them explicitly
      // and exclude them from the "scheduled" count so the run reads "N drafted", not "N scheduled / 0 sent".
      if (emailsDrafted > 0) parts.push(`${emailsDrafted} drafted`);
      // Still in the send queue: everything queued that has not been sent, failed, drafted,
      // bounced or skipped. Skipped recipients are settled, not waiting, so they come off here
      // too. So does a bounce: the email went out (it is listed as "N bounced" below), and the
      // live send-history overlay drops bounced rows out of emails_sent, so without this a step
      // that bounced once read "48 sent · 1 pending · 1 bounced" for good (1,142 of 19,576 prod
      // email steps since July, mostly Hologram).
      outstanding = outstandingFromCounts({
        scheduled, drafted: emailsDrafted, sent: emailsSent, failed: emailsFailed, bounced, skipped: emailSkip,
      });
      if (emailsSent === 0 && outstanding > 0 && (exec.status === 'completed' || (!terminal && emailsFailed === 0))) {
        parts.push(`${outstanding} scheduled`);
      } else if (emailsSent > 0) {
        const pure = Math.max(0, emailsSent - replied);
        if (pure > 0) parts.push(`${pure} sent`);
        if (outstanding > 0) parts.push(`${outstanding} pending`);
      }
      if (emailsFailed > 0) parts.push(`${emailsFailed} failed`);
      if (emailSkip > 0) parts.push(renderSkipLabel(emailSkip, emailSkips.reasons, 'skipped'));
      if (capHold) parts.push(dailyCapHoldSummary(capHold));
      if (replied > 0) parts.push(`${replied} replied`);
      if (bounced > 0) parts.push(`${bounced} bounced`);
      done = emailsSent || successful;
      summary = parts.join(' · ');
    }
  }

  // A native LinkedIn sequence (invite + messages in one campaign) reports its progress for the
  // whole campaign, not per touch: LinkedIn's "messaged" status can't say which message went out
  // (an accepted invite note can set it), so the counts are labelled overall and no single step
  // is credited.
  const nativeProgress = exec.play_type === 'heyreach_campaign'
    ? linkedInSequenceProgress(agent.live_lead_stats)
    : null;
  if (nativeProgress) summary = nativeProgress;

  if (!summary && !isControl) {
    const parts: string[] = [];
    if (exec.play_type === 'email_campaign' && (replied > 0 || bounced > 0)) {
      const pure = Math.max(0, successful - replied);
      if (pure > 0) parts.push(`${pure} sent`);
      if (replied > 0) parts.push(`${replied} replied`);
      if (bounced > 0) parts.push(`${bounced} bounced`);
    } else if (exec.play_type === 'ai_agent' && contacts > 1 && successful > 0 && successful < contacts) {
      parts.push(`${contacts} contacts`, successful === 1 ? '1 sequence' : `${successful} processed`);
    } else {
      const verb = exec.play_type === 'crm_add' ? 'added' : exec.play_type === 'webhook' ? 'triggered' : 'processed';
      // Records this step actually acted on — independent of audience size.
      // `contacts` can be inflated by people_count (the whole audience), so it
      // must NOT be reported as "processed" when the step only handled a few
      // records or every record failed (#1736: a step showed "346 processed"
      // while every one of the 3 actually-handled records failed).
      const handled = Math.max(resultsCount, successful + failed);
      if (successful > 0) parts.push(`${successful} ${verb}`);
      else if (handled > 0) parts.push(`${handled} ${verb}`);
      else if (exec.status === 'completed' && contacts > 0) parts.push(`${contacts} ${verb}`);
      if (failed > 0) parts.push(`${failed} failed`);
    }
    if (skipped > 0) parts.push(renderSkipLabel(skipped, skipReasons, 'excluded'));
    if (parts.length === 0 && contacts > 0) parts.push(`${contacts} contacts`);
    summary = parts.join(' · ');
    if (done === 0 && exec.status === 'completed' && contacts > 0 && failed === 0) done = contacts;
  }

  // A step that stopped (or is still running) mid-batch reports settled-record
  // progress; its run-level metrics stay empty until it finishes, so the
  // progress counts are the only honest summary of what it actually did.
  const progressLine = isControl ? null : formatStepProgress(exec);
  if (progressLine) summary = progressLine;

  // A gated LinkedIn step accounts for the people it is holding, whichever branch above
  // wrote the summary: they are in no other count, so without this the step's own numbers
  // do not add up to its input. See connectionGateTally.
  const gate = connectionGateTally(exec);
  if (gate) {
    const gateParts = connectionGateParts(gate);
    if (gateParts.length > 0) summary = [summary, ...gateParts].filter(Boolean).join(' · ');
  }

  // countEmailSkips already holds the fullest source, less the redelivery audit rows the server
  // counted as skips; the raw server figure would put those rows back.
  const skippedForDisplay = exec.play_type === 'email_campaign' ? emailSkip : skipped;

  return { isControl, contacts, done, previewed, scheduled, drafted: emailsDrafted, outstanding, failed: failedForDisplay, skipped: skippedForDisplay, opens, clicks, replies, hasEngagement, summary };
}

/**
 * "LinkedIn sequence overall: 40 invited · 12 accepted · 9 messaged" from the server's
 * agent_metrics.live_lead_stats.linkedin_sequence, or null when the step isn't a native sequence.
 */
function linkedInSequenceProgress(liveLeadStats: unknown): string | null {
  const stats = (liveLeadStats && typeof liveLeadStats === 'object'
    ? (liveLeadStats as Record<string, unknown>).linkedin_sequence
    : null) as Record<string, unknown> | null | undefined;
  if (!stats || typeof stats !== 'object' || stats.aggregate !== true) return null;
  return `LinkedIn sequence overall: ${num(stats, 'invited')} invited · ${num(stats, 'accepted')} accepted · ${num(stats, 'messaged')} messaged`;
}

/* ------------------------------------------------------------------ */
/* The connection-acceptance gate                                      */
/* ------------------------------------------------------------------ */

export interface ConnectionGateTally {
  /** The gate is still on the clock: these people get the message if they accept. */
  waiting: boolean;
  /** People held behind the gate right now. */
  pending: number;
  /** Settled: the request was never accepted in the window, so the message never goes. */
  abandoned: number;
  /** People the gate could not evaluate — an operational fault, not a prospect outcome. */
  providerFailed: number;
}

/**
 * What a gated LinkedIn message step is holding, from the worker's own counters.
 *
 * A `heyreach_campaign` step that follows a connection request will not message anyone
 * who has not accepted it yet (audience-processor's `connectionAcceptanceGate`), and those
 * people are in none of `successful`, `failed` or `skipped`. So the step's numbers did not
 * add up to its input and nothing on the screen said why: audience 10258's run #1 step 6
 * read "2 processed · 3 excluded" against 22 people handed over, and the missing 19 only
 * existed in `agent_metrics` (mb-frontend#5479, asked on a live customer call 2026-10-26).
 *
 * Waiting and abandoned are deliberately separate numbers. One is a person who may still
 * get the message; the other is a person who never will, and telling a rep to keep waiting
 * on them is a wrong answer rather than a vague one. Measured on prod the same day, ~48% of
 * acceptances arrive more than 24 hours after the request, so both states are common.
 */
export function connectionGateTally(exec: Pick<RunExecution, 'play_type' | 'metrics'>): ConnectionGateTally | null {
  if (exec.play_type !== 'heyreach_campaign') return null;
  const agent = ((exec.metrics || {}) as Record<string, unknown>).agent_metrics as Record<string, unknown> | undefined;
  if (!agent) return null;
  const pending = num(agent, 'connection_gate_pending');
  const abandoned = num(agent, 'connection_gate_pending_abandoned');
  const providerFailed = num(agent, 'connection_gate_provider_failed');
  // `connection_gate_waiting` is the worker's own boolean and the only thing that says
  // whether the clock is still running; `pending` stays populated after it stops.
  const waiting = agent.connection_gate_waiting === true;
  if (!waiting && pending === 0 && abandoned === 0 && providerFailed === 0) return null;
  return { waiting, pending, abandoned, providerFailed };
}

/**
 * The summary parts a gate contributes, in the words the rep needs. `abandoned` is
 * subtracted from `pending` when both are set, because the worker counts an abandoned
 * person in both and reporting them twice makes the step over-account for its input.
 */
function connectionGateParts(gate: ConnectionGateTally): string[] {
  const parts: string[] = [];
  const stillWaiting = gate.waiting ? Math.max(0, gate.pending - gate.abandoned) : 0;
  if (stillWaiting > 0) parts.push(`${stillWaiting} awaiting connection acceptance`);
  if (gate.abandoned > 0) parts.push(`${gate.abandoned} never accepted the connection request`);
  if (gate.providerFailed > 0) parts.push(`${gate.providerFailed} could not be checked on LinkedIn`);
  return parts;
}

/** Format a delay step's configured duration, e.g. "48 hours" / "3 days". */
export function formatDelayLabel(exec: RunExecution): string | null {
  const cfg = (exec.custom_action_details?.configuration ?? null) as { unit?: string; duration?: number } | null;
  if (!cfg?.unit || !cfg?.duration) return null;
  return `${cfg.duration} ${cfg.unit}`;
}

/* ------------------------------------------------------------------ */
/* Step status derivation (adds the honest "Blocked" state)           */
/* ------------------------------------------------------------------ */

/** Step states that represent a genuinely finished outcome for that step. */
const TERMINAL_STEP_STATUSES: ReadonlySet<StepHealth> = new Set(['delivered', 'partial', 'failed', 'skipped']);

/**
 * Step states that release the steps AFTER them from the out-of-order gate in
 * {@link deriveStepStatus}. 'sending' belongs here even though such a step has not finished:
 * the backend hands off to the next play when the step row flips to 'completed' — at its
 * FIRST settled recipient, not its last — so a later step legitimately holds real results
 * while an earlier one is still draining its send queue. Gating on 'sending' would hide
 * those behind "Queued", trading one wrong badge for another.
 */
const CHAIN_RELEASING_STEP_STATUSES: ReadonlySet<StepHealth> = new Set([...TERMINAL_STEP_STATUSES, 'sending']);

/**
 * Map a backend ExecutionStatus to the display vocabulary, deriving "blocked"
 * when a not-yet-run step sits downstream of a failed step in the same run.
 * Does not apply the earlier-step gate — see the exported `deriveStepStatus`.
 */
function rawStepStatus(
  exec: RunExecution,
  executions: RunExecution[],
): StepHealth {
  const raw = exec.status;

  if (raw === 'completed') {
    // The backend marks a step 'completed' even when every record errored.
    // Surface the honest outcome from the per-record metrics (#1736: an ai_agent
    // step that failed all of its records showed "Delivered"). Only downgrade
    // when there are real per-record failures — skips/zero-metric control steps
    // stay 'delivered'.
    //
    // linkedin_enrichment alone needs deriveStepMetrics's enriched/not-found
    // reclassification (#3379). Every other play type keeps reading the raw,
    // frozen dispatch-time successful/failed snapshot: deriveStepMetrics's
    // per-play-type overrides (e.g. email_campaign preferring the live
    // agent_metrics.emails_sent aggregate over the results-array recount) were
    // built for the metrics *summary*, not status derivation, and can diverge
    // from the raw snapshot enough to flip a step's status.
    let ok: number;
    let bad: number;
    let skipped = 0;
    if (exec.play_type === 'linkedin_enrichment') {
      const m = deriveStepMetrics(exec);
      ok = m.done;
      bad = m.failed;
    } else {
      const m = (exec.metrics || {}) as Record<string, unknown>;
      ok = num(m, 'successful');
      bad = num(m, 'failed');
      skipped = num(m, 'skipped');
    }
    if (bad > 0 && ok === 0) return 'failed';
    // No terminal verdict while the send queue is still draining: 'delivered' is the claim a
    // customer called out ("why does it say completed when there are still 20 pending emails
    // to go out?"), and 'partial' would be just as premature — the remaining sends may yet
    // succeed. A step where nothing at all worked keeps 'failed' above; that verdict is in.
    if (isStillSendingEmails(exec)) return 'sending';
    if (bad > 0 && ok > 0) return 'partial';
    // A skip is neither a success nor an error, and the original fix read only the two. So a
    // step that reached every contact and sent to none of them — 21 scheduled, 20 suppressed,
    // 1 unsubscribed, 0 sent (Jumio, 2026-09-22) — rendered emerald "Delivered", whose own
    // tooltip says "every contact was processed successfully at this step". The rep read that,
    // concluded the campaign was stuck rather than blocked, and stopped and re-ran it three
    // times. `skipped` is only set by steps that count records, so control steps with no
    // metrics are unaffected and stay 'delivered'.
    //
    // Deliberately only when NOTHING succeeded. A step that reached some people and skipped
    // others is working: skipping a prospect who already replied is the correct outcome and the
    // commonest skip there is, so downgrading a mixed step would repaint healthy campaigns amber.
    if (skipped > 0 && ok === 0) return 'skipped';
    // A gated LinkedIn step the backend calls 'completed' is not finished while the gate is
    // still on the clock, and it did not deliver to everyone when the gate gave people up.
    // Audience 10258's step 6 messaged 2 of 22 and rendered emerald "Delivered", whose own
    // tooltip says every contact was processed successfully.
    const gate = connectionGateTally(exec);
    if (gate) {
      if (gate.waiting && gate.pending > gate.abandoned) return 'waiting';
      if (gate.abandoned > 0 || gate.providerFailed > 0) return ok > 0 ? 'partial' : 'skipped';
    }
    return 'delivered';
  }
  if (raw === 'partial') return 'partial';
  if (raw === 'failed') return 'failed';
  if (raw === 'in_progress' || raw === 'generating_content') return 'running';
  if (raw === 'waiting') {
    // Every contact was held for the inbox daily limit and a later run has since picked them
    // up: this step is finished for this run, not waiting on anything.
    const capHold = heldForDailyCap(exec);
    return capHold?.all_held && capHold.carried_over ? 'skipped' : 'waiting';
  }
  if (raw === 'skipped') return 'skipped';

  // pending (or null execution) — blocked if any earlier step failed
  const anyEarlierFailed = executions.some(
    (e) => e.sequence_order < exec.sequence_order && e.status === 'failed',
  );
  return anyEarlierFailed ? 'blocked' : 'queued';
}

/**
 * Map a backend ExecutionStatus to the display vocabulary. Wraps `rawStepStatus`
 * with an earlier-step gate: a step is never shown with its own terminal result
 * (delivered/partial/failed/skipped) while any earlier step in the sequence
 * hasn't reached a terminal state yet. Backend writes can land slightly out of
 * order relative to a single "get run details" snapshot (e.g. the next step's
 * failure/completion row committing before the previous step's row does), and
 * without this gate the panel could show a later touch's real result while an
 * earlier step still displays as running — a state that reads as broken to a
 * user even when each row's own data is individually correct.
 */
export function deriveStepStatus(
  exec: RunExecution,
  executions: RunExecution[],
): StepHealth {
  const own = rawStepStatus(exec, executions);
  if (!TERMINAL_STEP_STATUSES.has(own)) return own;

  const earlierNonTerminal = executions.some((e) => {
    if (e.sequence_order >= exec.sequence_order) return false;
    return !CHAIN_RELEASING_STEP_STATUSES.has(rawStepStatus(e, executions));
  });
  return earlierNonTerminal ? 'queued' : own;
}

export interface StepStatusHint {
  label: string;
  desc: string;
}

/**
 * Plain-language override when a step is queued because of business-hours gating
 * (common on LinkedIn/email touches) rather than a delay timer.
 */
export function getStepStatusHint(
  exec: RunExecution,
  stepHealth: StepHealth,
  estimatedDate?: Date | null,
): StepStatusHint | null {
  const capHold = describeDailyCapHold(exec);
  if (capHold) {
    if (stepHealth === 'waiting' || stepHealth === 'skipped') return { label: capHold.label, desc: capHold.desc };
    // Some contacts went out and the rest were held: keep the outcome, explain the hold.
    if (stepHealth === 'delivered' || stepHealth === 'partial') {
      return { label: STEP_STATUS_META[stepHealth].label, desc: capHold.desc };
    }
  }
  // The gate explains itself before business hours do: a LinkedIn step waiting on
  // acceptances is not waiting on a send window, and "Outside business hours" sent a rep
  // looking at the campaign's schedule for an answer that was never there.
  const gate = connectionGateTally(exec);
  if (gate) {
    const stillWaiting = gate.waiting ? Math.max(0, gate.pending - gate.abandoned) : 0;
    if (stillWaiting > 0) {
      return {
        label: 'Awaiting connection acceptance',
        desc: `${stillWaiting} ${stillWaiting === 1 ? 'person has' : 'people have'} not accepted the connection request yet, so this step is holding their message. It sends automatically once they accept; LinkedIn acceptances often take several days.`,
      };
    }
    if (gate.abandoned > 0 && (stepHealth === 'partial' || stepHealth === 'skipped')) {
      return {
        label: STEP_STATUS_META[stepHealth].label,
        desc: `${gate.abandoned} ${gate.abandoned === 1 ? 'person never accepted' : 'people never accepted'} the connection request inside the waiting window, so this step will not message them. Re-running the campaign is what gives them a fresh request.`,
      };
    }
  }

  // Say how many are actually left rather than the generic blurb — "18 of 30 still queued"
  // is the answer the rep came for, and it reconciles the badge with the summary beside it.
  if (stepHealth === 'sending') {
    const mx = deriveStepMetrics(exec);
    const outstanding = mx.outstanding;
    return {
      label: STEP_STATUS_META.sending.label,
      desc: outstanding > 0 && mx.scheduled > 0
        ? `${outstanding} of ${mx.scheduled} ${outstanding === 1 ? 'email is' : 'emails are'} still queued. Sends are paced across your mailboxes, so this finishes on its own — the counts update as they land.`
        : STEP_STATUS_META.sending.desc,
    };
  }

  if (stepHealth !== 'queued' && stepHealth !== 'waiting') return null;
  if (exec.play_type === 'delay') return null;
  if (!stepUsesBusinessHours(exec)) return null;

  const timeZone = getStepScheduleTimezone(exec) || 'UTC';
  const waitingForHours = isCurrentlyOutsideBusinessHours(timeZone);
  const estLabel = estimatedDate ? formatEstimatedDate(estimatedDate) : '';
  const tzLabel = tzAbbrev(new Date(), timeZone) || timeZone;

  if (!waitingForHours && !estLabel) return null;

  const windowCopy = 'Mon–Fri, 9:00 AM–5:00 PM';
  const resumeCopy = estLabel
    ? `Next send window: ${estLabel} (${tzLabel}).`
    : `It will resume in the next ${windowCopy} window (${tzLabel}).`;

  return {
    label: waitingForHours ? 'Outside business hours' : 'Scheduled',
    desc: `This step only runs during ${windowCopy} in ${timeZone}. ${resumeCopy}`,
  };
}

/* ------------------------------------------------------------------ */
/* Run health derivation (the honest "Stalled" state)                 */
/* ------------------------------------------------------------------ */

export interface RunHealthResult {
  status: RunHealth;
  /** Headline shown in the slim note. */
  title: string;
  /** Supporting sentence (omitted for some alert states). */
  body: string;
  /** Whether this is an attention-grabbing state (tinted note + accent). */
  alert: boolean;
  /** Compact facts surfaced in the Run details card for problem runs. */
  meta: { label: string; value: string }[];
}

export function runStatusToHealth(status: RunStatus): RunHealth {
  switch (status) {
    case 'completed': return 'delivered';
    case 'partial': return 'partial';
    case 'failed': return 'failed';
    case 'in_progress': return 'running';
    case 'skipped': return 'partial';
    // The backend flags a dangling/overdue chain (lost hand-off) as 'stalled'.
    // Without this case it fell through to 'pending' — a broken mid-sequence run
    // rendered as "Pending" (looks like it never started).
    case 'stalled': return 'stalled';
    case 'pending':
    default: return 'pending';
  }
}

/**
 * Derive an honest run health from the run row + its executions. The key fix:
 * a run the backend marks "completed"/"partial" is surfaced as **Stalled** when
 * a step failed and later steps never ran (contacts stuck mid-sequence).
 */
export function deriveRunHealth(
  run: Run,
  executions: RunExecution[] | undefined,
  isTest: boolean,
): RunHealthResult {
  const base = runStatusToHealth(run.status);

  // Without details we can only report the backend status.
  if (!executions || executions.length === 0) {
    return baseHealthCopy(base, run, isTest, executions);
  }

  const ordered = [...executions].sort((a, b) => a.sequence_order - b.sequence_order);
  const failedIdx = ordered.findIndex((e) => e.status === 'failed');
  const hasFailed = failedIdx >= 0;
  const stuckAfterFailure = hasFailed && ordered
    .slice(failedIdx + 1)
    .some((e) => ['pending', 'waiting'].includes(e.status) || e.execution_id === null);

  if (hasFailed && stuckAfterFailure && run.status !== 'in_progress') {
    const failedStep = ordered[failedIdx];
    const stepNum = failedIdx + 1;
    const stuck = ordered
      .slice(failedIdx)
      .reduce((acc, e) => acc + num(e.metrics, 'total_contacts'), 0);
    const lastDelivered = [...ordered.slice(0, failedIdx)].reverse()
      .find((e) => e.status === 'completed' || e.status === 'partial');
    const reason = humanizeError(failedStep.error_type, failedStep.error);
    return {
      status: 'stalled',
      alert: true,
      title: `This run stopped at step ${stepNum} — later steps were never reached`,
      body: `“${stepName(failedStep)}” ${isInterruptedStep(failedStep) ? 'was interrupted before it finished' : 'failed'}, so the steps after it never ran.${stuck > 0 ? ` ${stuck} contact${stuck === 1 ? ' is' : 's are'} stuck mid-sequence.` : ''}`,
      meta: [
        ...(lastDelivered ? [{ label: 'Last delivered step', value: stepName(lastDelivered) }] : []),
        { label: 'Failure', value: reason },
        ...(stuck > 0 ? [{ label: 'Contacts stuck', value: String(stuck) }] : []),
      ],
    };
  }

  // Backend flagged a dangling chain with NO failed step: a step completed, but
  // its next never ran and the expected fire time is well past (lost/overdue
  // hand-off). Surface it honestly instead of "Pending"/"Scheduled".
  if (base === 'stalled') {
    const lastDone = [...ordered].reverse().find((e) => e.status === 'completed' || e.status === 'partial');
    const stuckStep = ordered.find((e) => e.status === 'waiting' || e.status === 'pending' || e.execution_id === null);
    return {
      status: 'stalled',
      alert: true,
      title: 'This run stalled — the next step is overdue',
      body: `“${lastDone ? stepName(lastDone) : 'A step'}” completed, but ${stuckStep ? `“${stepName(stuckStep)}”` : 'the next step'} hasn’t run and its scheduled time has passed. The hand-off was likely lost — re-run the campaign to resume the sequence.`,
      meta: [
        ...(lastDone ? [{ label: 'Last completed', value: stepName(lastDone) }] : []),
        ...(stuckStep ? [{ label: 'Stuck at', value: stepName(stuckStep) }] : []),
      ],
    };
  }

  const capNote = runDailyCapNote(ordered, base);
  if (capNote) return { ...capNote, alert: true, meta: [] };

  // Every step has been recorded done and the run is only waiting on its send queue to drain.
  // The generic "Steps are executing now" sends a reader hunting for a step that is running —
  // there is none — so name the real remainder and say that it needs nothing from them.
  //
  // Also covers a backend that still returns 'completed' while the queue drains (the status
  // Krishna saw on 2026-10-29): an email step flips to 'completed' at its FIRST settled
  // recipient. Without this branch the header kept a green "Delivered" tick beside a step
  // that said "10 sent · 20 pending". Failed / stalled / scheduled stay as they are.
  if (base === 'running' || base === 'delivered' || base === 'partial') {
    const draining = ordered.filter((e) => isStillSendingEmails(e));
    if (draining.length > 0) {
      const left = draining.reduce((acc, e) => acc + deriveStepMetrics(e).outstanding, 0);
      const queued = draining.reduce((acc, e) => acc + deriveStepMetrics(e).scheduled, 0);
      return {
        status: 'running',
        alert: false,
        title: left === 1 ? 'One email still to go out' : `${left} emails still to go out`,
        body: 'Sends are paced across your mailboxes to protect deliverability, so a step finishes over minutes or hours rather than all at once. Nothing is stuck and nothing is needed from you — the counts update as each email lands.',
        // Only the remainder is stated, not a "sent" figure: what has left the queue includes
        // skips and failures, and calling that total "sent" is the kind of near-miss number a
        // reader reconciles against the step rows and finds wrong.
        meta: queued > 0 ? [{ label: 'Still queued', value: `${left} of ${queued}` }] : [],
      };
    }
  }

  return baseHealthCopy(base, run, isTest, ordered);
}

function baseHealthCopy(
  status: RunHealth,
  run: Run,
  isTest: boolean,
  executions: RunExecution[] | undefined,
): RunHealthResult {
  const replies = (executions || [])
    .filter((e) => e.play_type === 'email_campaign')
    .reduce((acc, e) => acc + num(e.metrics, 'replied', 'replies'), 0);
  const firstFailed = (executions || []).find((e) => e.status === 'failed');

  switch (status) {
    case 'delivered':
      return {
        status, alert: false,
        title: isTest ? 'Test run — content generated, nothing sent' : 'Ran end-to-end',
        body: isTest
          ? 'A dry run to preview generated content. No real messages were sent and no contacts were affected.'
          : `All steps completed for every eligible contact${replies > 0 ? ` · ${replies} repl${replies === 1 ? 'y' : 'ies'}` : ''}.`,
        meta: [],
      };
    case 'partial':
      return {
        status, alert: true,
        title: 'Some contacts didn’t complete every step',
        body: firstFailed
          ? `“${stepName(firstFailed)}” didn’t succeed for everyone (${humanizeError(firstFailed.error_type, firstFailed.error)}). The rest of the sequence continued.`
          : 'A bounce, skip, or failure along the way meant not every contact received every step.',
        meta: firstFailed ? [{ label: 'Issue at', value: stepName(firstFailed) }] : [],
      };
    case 'failed':
      return {
        status, alert: true,
        title: 'This run failed',
        body: !firstFailed
          ? 'The run failed before producing results.'
          : isInterruptedStep(firstFailed)
            ? `“${stepName(firstFailed)}” was interrupted. ${INTERRUPTED_STEP_DETAIL}`
            : humanizeError(firstFailed.error_type, firstFailed.error),
        meta: [],
      };
    case 'running':
      return { status, alert: false, title: 'Run in progress', body: 'Steps are executing now — this view updates as they complete.', meta: [] };
    case 'stalled':
      return {
        status, alert: true,
        title: 'This run stalled — the next step is overdue',
        body: 'A step completed but the next one hasn’t run and its scheduled time has passed. The hand-off was likely lost — re-run the campaign to resume the sequence.',
        meta: [],
      };
    case 'pending':
    default:
      return { status, alert: false, title: 'Scheduled', body: 'Queued to run. Delay steps will advance it automatically.', meta: [] };
  }
}

/* ------------------------------------------------------------------ */
/* Inbox daily-limit holds                                            */
/* ------------------------------------------------------------------ */

/**
 * Warmup inboxes have a daily send limit. When an email step's inboxes are full, the
 * worker holds the remaining contacts and the campaign's next run picks them up. The
 * backend reports it as a bare "waiting" status, which read as a timed delay (Hologram,
 * 2026-10-08), so these helpers turn the server's structured `daily_cap_hold` into
 * plain English. Reason codes never reach the copy.
 */

function heldForDailyCap(exec: Pick<RunExecution, 'daily_cap_hold'>): DailyCapHold | null {
  const hold = exec.daily_cap_hold;
  return hold && hold.held > 0 ? hold : null;
}

const contactsCount = (n: number): string => `${n} contact${n === 1 ? '' : 's'}`;

/** "Thu Oct 9 · 6:00 AM PDT", in the viewer's timezone. */
function formatResumeTime(iso: string, withZone = true): string {
  const date = new Date(iso);
  const tz = displayTimezone();
  const zone = withZone ? tzAbbrev(date, tz) : '';
  return `${formatAbsolute(date, tz)}${zone ? ` ${zone}` : ''}`;
}

/**
 * Time-column label for contacts still waiting on the inbox limit: "resumes Thu Oct 9 ·
 * 6:00 AM" (title carries the timezone), or "resumes on next run" when the campaign has no
 * scheduled run. Null when nothing is waiting, so callers fall back to their usual time.
 */
export function dailyCapResumeLabel(exec: Pick<RunExecution, 'daily_cap_hold'>): { text: string; title: string } | null {
  const hold = heldForDailyCap(exec);
  if (!hold || hold.carried_over) return null;
  if (!hold.resumes_at) {
    return { text: 'resumes on next run', title: 'Resumes the next time the campaign runs' };
  }
  return {
    text: `resumes ${formatResumeTime(hold.resumes_at, false)}`,
    title: `Resumes ${formatResumeTime(hold.resumes_at)}`,
  };
}

function dailyCapHoldSummary(hold: DailyCapHold): string {
  return hold.carried_over
    ? `${hold.held} moved to a later run`
    : `${hold.held} waiting for inbox limit`;
}

function listInboxes(hold: DailyCapHold): string {
  const boxes = hold.mailboxes;
  const limits = new Set(boxes.map((b) => b.daily_limit));
  const sameLimit = limits.size === 1 ? boxes[0].daily_limit : null;
  const named = boxes.map((b) => (sameLimit == null && b.daily_limit != null ? `${b.email} (${b.daily_limit} a day)` : b.email));
  const list = named.length > 1 ? `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}` : named[0];
  if (sameLimit == null) return list;
  return `${list} (${sameLimit} a day${boxes.length > 1 ? ' each' : ''})`;
}

export interface DailyCapHoldCopy {
  label: string;
  desc: string;
}

/**
 * Plain-English copy for a step that held contacts for the inbox daily limit, or null.
 * `subject` names the step in run-level copy ("“Email” …"); defaults to "this step".
 */
export function describeDailyCapHold(
  exec: Pick<RunExecution, 'daily_cap_hold'>,
  subject = 'this step',
): DailyCapHoldCopy | null {
  const hold = heldForDailyCap(exec);
  if (!hold) return null;
  const contacts = contactsCount(hold.held);
  const verb = hold.held === 1 ? 'was' : 'were';
  const inboxes = hold.mailboxes.length > 0 ? listInboxes(hold) : '';

  if (hold.carried_over) {
    const why = hold.all_held
      ? `Every inbox on ${subject} had sent its daily limit`
      : `The inboxes on ${subject} reached their daily limit`;
    return {
      label: 'Moved to a later run',
      desc: `${why}, so ${contacts} ${verb} not emailed in this run. They went back into the campaign to be sent in a later run.${inboxes ? ` Inboxes that were at their limit: ${inboxes}.` : ''}`,
    };
  }

  const why = hold.all_held
    ? `Every inbox on ${subject} has sent its daily limit`
    : `The inboxes on ${subject} reached their daily limit`;
  const waiting = hold.held === 1 ? 'is waiting' : 'are waiting';
  const when = hold.resumes_at
    ? `when the campaign next runs: ${formatResumeTime(hold.resumes_at)}`
    : 'the next time the campaign runs';
  return {
    label: 'Waiting for inbox limit',
    desc: `${why}, so ${contacts} ${waiting}. They go out ${when}.${inboxes ? ` Inboxes at their daily limit: ${inboxes}.` : ''} The limit protects your inboxes while they warm up; nothing is broken.`,
  };
}

/**
 * Run states the inbox-limit wording replaces. Partial, failed, stalled and running runs keep
 * their own headline; the hold is still explained on the step.
 */
const INBOX_LIMIT_RUN_BASES: ReadonlySet<RunHealth> = new Set(['pending', 'scheduled', 'delivered']);

/**
 * Status for a run in the run picker. Same wording as the run note: "Waiting for inbox limit"
 * while an email step still holds contacts, "Moved to a later run" for a run that stalled
 * only because its contacts went to a later run. Otherwise the run's own status.
 */
export function runPickerStatus(run: Pick<Run, 'status' | 'inbox_limit_hold'>): { label: string; tone: Tone } {
  const base = runStatusToHealth(run.status);
  if (run.inbox_limit_hold === 'waiting' && INBOX_LIMIT_RUN_BASES.has(base)) {
    return { label: 'Waiting for inbox limit', tone: RUN_STATUS_META.scheduled.tone };
  }
  if (run.inbox_limit_hold === 'moved' && base === 'pending') {
    return { label: 'Moved to a later run', tone: RUN_STATUS_META.pending.tone };
  }
  return RUN_STATUS_META[base];
}

export interface RunDailyCapNote {
  status: RunHealth;
  title: string;
  body: string;
}

/**
 * Run-level note when contacts are held for the inbox daily limit. Without it, a run
 * whose email step held everyone read "Pending/Scheduled — delay steps will advance it".
 */
export function runDailyCapNote(executions: RunExecution[] | undefined, base: RunHealth): RunDailyCapNote | null {
  if (!executions || !INBOX_LIMIT_RUN_BASES.has(base)) return null;
  const waiting = executions.find((e) => {
    const hold = heldForDailyCap(e);
    return hold && !hold.carried_over;
  });
  if (waiting) {
    const copy = describeDailyCapHold(waiting, `“${stepName(waiting)}”`);
    return copy && { status: 'scheduled', title: copy.label, body: copy.desc };
  }
  // Every contact held and since picked up by a later run: this run will never move again.
  const moved = base === 'pending' ? executions.find((e) => heldForDailyCap(e)?.all_held) : undefined;
  if (moved) {
    const copy = describeDailyCapHold(moved, `“${stepName(moved)}”`);
    return copy && { status: 'pending', title: 'Contacts moved to a later run', body: copy.desc };
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Interrupted steps, partial progress & returned contacts            */
/* ------------------------------------------------------------------ */

/**
 * Customer copy for a step the system force-failed after it stopped mid-run
 * (e.g. a deploy restart). mb-server's stuck-execution cleanup writes internal
 * text ("Marked failed: … stuck in in_progress for 2h (auto-cleanup).") that
 * must never be shown verbatim.
 */
export const INTERRUPTED_STEP_REASON = 'Step was interrupted';
export const INTERRUPTED_STEP_DETAIL = 'This step stopped before it finished and was ended automatically. Contacts it had not reached were not contacted by this step.';

/** Rows failed before mb-server stamped error_type 'interrupted' only carry the cleanup text. */
const LEGACY_INTERRUPTED_ERROR = /^Marked failed:.*stuck in .* \(auto-cleanup\)/i;

function isLegacyInterruptedError(message: string | null | undefined): boolean {
  return typeof message === 'string' && LEGACY_INTERRUPTED_ERROR.test(message.trim());
}

type StepErrorFields = Pick<RunExecution, 'error' | 'error_type'>;

/** True when the step was force-failed because it stopped before finishing. */
export function isInterruptedStep(step: StepErrorFields | null | undefined): boolean {
  if (!step) return false;
  return step.error_type === 'interrupted' || isLegacyInterruptedError(step.error);
}

/** The error text a customer should see for a step (interrupted copy replaces the raw cleanup text). */
export function customerStepError(step: StepErrorFields): string | null {
  return isInterruptedStep(step) ? INTERRUPTED_STEP_REASON : step.error;
}

const wholeCount = (value: unknown): number => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
);

function settledProgress(progress: StepProgress | null | undefined): StepProgress | null {
  if (!progress || typeof progress !== 'object') return null;
  const p = { completed: wholeCount(progress.completed), failed: wholeCount(progress.failed), pending: wholeCount(progress.pending) };
  return p.completed + p.failed + p.pending > 0 ? p : null;
}

/**
 * Compact progress line for a step that has not completed, e.g.
 * "265 done · 34 failed · 144 not reached". Step-type neutral on purpose: the
 * counts come from whatever records the step settled while it ran.
 */
export function formatStepProgress(exec: Pick<RunExecution, 'status' | 'progress'>): string | null {
  if (exec.status === 'completed') return null;
  const p = settledProgress(exec.progress);
  if (!p) return null;
  const parts = [`${p.completed} done`];
  if (p.failed > 0) parts.push(`${p.failed} failed`);
  if (p.pending > 0) parts.push(`${p.pending} ${exec.status === 'in_progress' ? 'remaining' : 'not reached'}`);
  return parts.join(' · ');
}

/**
 * Empty-state copy for a stopped step that did process contacts but saved no
 * per-contact results — "failed before producing results" would be untrue.
 */
export function describeStoppedStepProgress(exec: Pick<RunExecution, 'status' | 'progress'>): string | null {
  if (exec.status === 'completed' || exec.status === 'in_progress') return null;
  const p = settledProgress(exec.progress);
  if (!p) return null;
  const processed = p.completed + p.failed;
  if (processed === 0) return null;
  const total = processed + p.pending;
  return `This step stopped after processing ${processed} of ${total} contact${total === 1 ? '' : 's'}; per-contact results were not saved.`;
}

/** Copy for contacts handed back to the campaign (replaces the Resume action), or null when none were. */
export function formatContactsReturned(returned: number | null | undefined): string | null {
  const n = wholeCount(returned);
  if (n === 0) return null;
  return `${n} contact${n === 1 ? ' was' : 's were'} returned to this campaign and will be included the next time it runs.`;
}

/** Turn a worker error_type/message into a short, customer-safe phrase. */
export function humanizeError(type: string | null, message: string | null): string {
  if (type === 'interrupted' || isLegacyInterruptedError(message)) return INTERRUPTED_STEP_REASON;
  if (type) {
    const t = type.replace(/_/g, ' ').trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  const m = String(message ?? '').trim();
  if (!m || m.includes('{') || /stack trace/i.test(m)) return 'Step error';
  return m.length > 80 ? `${m.slice(0, 80)}…` : m;
}
